import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { Product } from "@/lib/types";
import { ConfigurationKey, ConfigurationValue, emptyJsonValue, useDraftError } from "./configuration-value-editor";
import { useConfigurationRowIds } from "./configuration-row-ids";

const types = ["boolean", "number", "string", "list", "version"];
export type ConfigurationEntitlement = { type: string; value: unknown; [key: string]: unknown };

export function ConfigurationEntitlements({ catalog, value, onChange }: {
  catalog: Product["feature_catalog"]; value: Record<string, unknown>; onChange: (value: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const rowIds = useConfigurationRowIds();
  const keys = [...new Set([...Object.keys(catalog ?? {}), ...Object.keys(value)])];
  const remove = (key: string) => { rowIds.remove(key); onChange(Object.fromEntries(Object.entries(value).filter(([k]) => k !== key))); };
  return <div className="min-w-0">
    <div className="divide-y divide-line">
      {keys.map((key) => {
        const present = Object.hasOwn(value, key);
        const raw = value[key];
        const entry = raw && typeof raw === "object" && !Array.isArray(raw) ? raw as ConfigurationEntitlement : undefined;
        const catalogType = Object.hasOwn(catalog ?? {}, key) ? catalog?.[key] : undefined;
        const type = typeof entry?.type === "string" ? entry.type : catalogType ?? "boolean";
        const update = (nextType: string, nextValue: unknown) => onChange({ ...value, [key]: { ...entry, type: nextType, value: nextValue } });
        return <div key={rowIds.key(key)} className="min-w-0 space-y-3 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {catalogType ? <span className="min-w-0 break-all font-mono text-xs">{key}</span>
              : <ConfigurationKey label={t("configurationEditor.entitlementKey", "Entitlement key")} value={key} keys={keys} singleLine
                onChange={(next) => { rowIds.rename(key, next); onChange(Object.fromEntries(Object.entries(value).map(([k, v]) => [k === key ? next : k,
                  k === key && entry && Object.hasOwn(entry, "key") ? { ...entry, key: next } : v]))); }} />}
            <Button type="button" size="sm" variant="ghost" onClick={() => present ? remove(key) : update(type, emptyJsonValue(type))}
              aria-label={`${present ? t("configurationEditor.remove", "Remove") : t("configurationEditor.setValue", "Set value")} ${key}`}>
              {present ? t("configurationEditor.remove", "Remove") : t("configurationEditor.setValue", "Set value")}
            </Button>
          </div>
          {!present ? <p className="text-xs text-muted">{t("configurationEditor.notSet", "Not set")}</p>
            : <>
              {!catalogType && <p className="text-xs text-muted">{t("configurationEditor.outsideCatalog", "Outside Product catalog")}</p>}
              <div className="grid min-w-0 grid-cols-[minmax(0,8rem)_minmax(0,1fr)] items-start gap-2">
              <Select aria-label={`${key} type`} value={type}
                options={[...types, ...(!types.includes(type) ? [type] : [])].map((v) => ({ value: v, label: v }))}
                onValueChange={(next) => update(next, emptyJsonValue(next))} />
              <div className={type === "list" ? "col-span-2 min-w-0" : "min-w-0"}><EntitlementValue type={type} value={entry?.value} label={key} onChange={(next) => update(type, next)} /></div>
              </div>
              {catalogType && catalogType !== type && <p className="text-xs text-warning">{t("configurationEditor.catalogType", "Product catalog type: {{type}}", { type: catalogType })}</p>}
            </>}
        </div>;
      })}
    </div>
    <div className="px-4 py-3"><Button type="button" variant="outline" size="sm" onClick={() => {
      let key = "feature";
      while (keys.includes(key)) key += "_";
      onChange({ ...value, [key]: { type: "boolean", value: false } });
    }}>{t("configurationEditor.addEntitlement", "Add Entitlement")}</Button></div>
  </div>;
}

function EntitlementValue({ type, value, label, onChange }: { type: string; value: unknown; label: string; onChange: (value: unknown) => void }) {
  const { t } = useTranslation();
  const supported = types.includes(type);
  useDraftError(!supported);
  if (!supported) return <p className="text-xs text-danger">{t("configurationEditor.unsupportedType", "Choose a supported Entitlement type.")}</p>;
  return <ConfigurationValue label={label} value={value} onChange={onChange}
    fixedType={type === "list" ? "array" : type === "version" ? "string" : type as "boolean" | "number" | "string"} />;
}
